import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { rubrics as rubricsApi, tags as tagsApi, projects as projectsApi } from '../lib/api.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const tableHeader = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${colors.border};
`

const tableRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const col = (flex) => css`flex: ${flex}; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`

const sortableCol = (flex) => css`
  flex: ${flex};
  min-width: 0;
  cursor: pointer;
  user-select: none;
  &:hover { color: ${colors.text}; }
`

const tagChip = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Form styles ---

const formOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const formCard = css`
  ${card};
  width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`

const formTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.lg};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const criteriaSection = css`
  margin-top: ${space.md};
`

const criterionRow = css`
  display: flex;
  gap: ${space.sm};
  align-items: flex-start;
  padding: ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  margin-bottom: ${space.sm};
  background: ${colors.bgCard};
  transition: border-color 0.15s, box-shadow 0.15s;
`

const criterionRowDragging = css`
  opacity: 0.4;
`

const criterionRowDragOver = css`
  border-color: ${colors.brand};
  box-shadow: 0 0 0 1px ${colors.brand};
`

const dragHandle = css`
  cursor: grab;
  color: ${colors.textMuted};
  padding: 4px 2px;
  font-size: ${font.size.base};
  user-select: none;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  &:hover { color: ${colors.text}; }
  &:active { cursor: grabbing; }
`

const criterionNumber = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.semibold};
  width: 20px;
  text-align: center;
  padding-top: 8px;
  flex-shrink: 0;
`

const criterionDesc = css`
  ${input};
  flex: 1;
  min-height: 36px;
  resize: none;
  line-height: 1.4;
`

const criticalitySelect = css`
  padding: ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  min-width: 110px;
`

const removeBtn = css`
  padding: 6px 8px;
  border: none;
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  font-size: ${font.size.base};
  &:hover { color: ${colors.brand}; }
`

const addCriterionBtn = css`
  ${btnSecondary};
  width: 100%;
  justify-content: center;
  margin-top: ${space.sm};
`

const versionBadge = css`
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  padding: 1px 6px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
`

const changeSummaryInput = css`
  ${input};
  font-size: ${font.size.sm};
`

const shortcutHint = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.sm};
  padding: ${space.sm};
  background: ${colors.bg};
  border-radius: 4px;
`

// --- Helpers ---

const CRITICALITY_COLORS = {
  critical: colors.critical,
  important: colors.important,
  info: colors.info,
}

function emptyCriterion() {
  return { description: '', criticality: 'important', sortOrder: 0 }
}

// --- Component ---

export function RubricsView() {
  let projectId = null
  let project = null
  let rubricList = []
  let tagList = []
  let loading = true
  let showForm = false
  let editingRubric = null
  let formData = newFormData()
  let saving = false

  // Sort state
  let sortCol = null  // 'title' | 'tag'
  let sortDir = 'asc' // 'asc' | 'desc'

  function toggleSort(col) {
    if (sortCol === col) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc'
    } else {
      sortCol = col
      sortDir = 'asc'
    }
  }

  function sortedRubrics() {
    if (!sortCol) return rubricList
    const list = [...rubricList]
    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      let va, vb
      if (sortCol === 'title') {
        va = a.title.toLowerCase()
        vb = b.title.toLowerCase()
      } else {
        va = (getTagName(a.tag_id) || '').toLowerCase()
        vb = (getTagName(b.tag_id) || '').toLowerCase()
      }
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })
    return list
  }

  function sortIndicator(col) {
    if (sortCol !== col) return ' \u2195'
    return sortDir === 'asc' ? ' \u2191' : ' \u2193'
  }

  // Drag state
  let dragIndex = null
  let dragOverIndex = null

  function newFormData() {
    return {
      title: '',
      description: '',
      tagId: '',
      criteria: [emptyCriterion()],
      changeSummary: '',
    }
  }

  async function loadRubrics(pid) {
    loading = true
    try {
      const [rubs, tags] = await Promise.all([
        rubricsApi.list(pid),
        tagsApi.list(pid),
      ])
      rubricList = rubs
      tagList = tags
    } catch (err) {
      console.error('Failed to load rubrics:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  function openCreate() {
    editingRubric = null
    formData = newFormData()
    showForm = true
  }

  async function openEdit(rubric) {
    try {
      const full = await rubricsApi.get(projectId, rubric.id)
      editingRubric = full
      formData = {
        title: full.title,
        description: full.description || '',
        tagId: full.tag_id || '',
        criteria: full.criteria.map(c => ({
          description: c.description,
          criticality: c.criticality,
          sortOrder: c.sort_order,
        })),
        changeSummary: '',
      }
      if (formData.criteria.length === 0) formData.criteria = [emptyCriterion()]
      showForm = true
      m.redraw()
    } catch (err) {
      console.error('Failed to load rubric:', err)
    }
  }

  function closeForm() {
    showForm = false
    editingRubric = null
    saving = false
    dragIndex = null
    dragOverIndex = null
  }

  function addCriterion(afterIdx) {
    const idx = afterIdx != null ? afterIdx + 1 : formData.criteria.length
    formData.criteria.splice(idx, 0, emptyCriterion())
    // Focus the new textarea after render
    m.redraw()
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-criterion-index="${idx}"] textarea`)
      if (el) el.focus()
    })
  }

  function removeCriterion(idx) {
    if (formData.criteria.length > 1) {
      formData.criteria.splice(idx, 1)
    }
  }

  function onCriterionKeydown(e, idx) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      addCriterion(idx)
    }
    // Alt+Up / Alt+Down to move
    if (e.altKey && e.key === 'ArrowUp' && idx > 0) {
      e.preventDefault()
      const item = formData.criteria.splice(idx, 1)[0]
      formData.criteria.splice(idx - 1, 0, item)
      m.redraw()
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-criterion-index="${idx - 1}"] textarea`)
        if (el) el.focus()
      })
    }
    if (e.altKey && e.key === 'ArrowDown' && idx < formData.criteria.length - 1) {
      e.preventDefault()
      const item = formData.criteria.splice(idx, 1)[0]
      formData.criteria.splice(idx + 1, 0, item)
      m.redraw()
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-criterion-index="${idx + 1}"] textarea`)
        if (el) el.focus()
      })
    }
    // Backspace on empty criterion removes it
    if (e.key === 'Backspace' && formData.criteria[idx].description === '' && formData.criteria.length > 1) {
      e.preventDefault()
      formData.criteria.splice(idx, 1)
      const focusIdx = Math.max(0, idx - 1)
      m.redraw()
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-criterion-index="${focusIdx}"] textarea`)
        if (el) el.focus()
      })
    }
  }

  // Drag-and-drop handlers
  function onDragStart(idx, e) {
    dragIndex = idx
    e.dataTransfer.effectAllowed = 'move'
    // Required for Firefox
    e.dataTransfer.setData('text/plain', String(idx))
  }

  function onDragOver(idx, e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    dragOverIndex = idx
  }

  function onDrop(idx, e) {
    e.preventDefault()
    if (dragIndex != null && dragIndex !== idx) {
      const item = formData.criteria.splice(dragIndex, 1)[0]
      formData.criteria.splice(idx, 0, item)
    }
    dragIndex = null
    dragOverIndex = null
  }

  function onDragEnd() {
    dragIndex = null
    dragOverIndex = null
  }

  async function submitForm() {
    const validCriteria = formData.criteria.filter(c => c.description.trim())
    if (!formData.title.trim() || validCriteria.length === 0) return

    saving = true
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        criteria: validCriteria.map((c, i) => ({
          description: c.description.trim(),
          criticality: c.criticality,
          sortOrder: i,
        })),
      }

      if (editingRubric) {
        payload.tagId = formData.tagId || null
        payload.changeSummary = formData.changeSummary.trim() || undefined
        await rubricsApi.update(projectId, editingRubric.id, payload)
      } else {
        payload.tagId = formData.tagId || undefined
        await rubricsApi.create(projectId, payload)
      }
      closeForm()
      await loadRubrics(projectId)
    } catch (err) {
      console.error('Failed to save rubric:', err)
      saving = false
      m.redraw()
    }
  }

  function getTag(tagId) {
    return tagList.find(t => t.id === tagId) || null
  }

  function getTagName(tagId) {
    const tag = getTag(tagId)
    return tag ? tag.name : null
  }

  // Auto-grow textarea
  function autoGrow(e) {
    const el = e.target
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadRubrics(projectId)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Rubrics',
        ]),

        m('div', {
          style: `display: flex; justify-content: space-between; align-items: center; margin-bottom: ${space.lg};`,
        }, [
          m('h1', { class: pageTitle, style: 'margin-bottom: 0;' }, 'Rubrics'),
          m('button', { class: btnPrimary, onclick: openCreate }, 'New rubric'),
        ]),

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : rubricList.length === 0
            ? m('div', { class: emptyState }, [
                m('p', 'No rubrics yet.'),
                m('p', { style: `margin-top: ${space.sm};` },
                  'Create a rubric to start evaluating sessions.'),
              ])
            : m('div', { class: card }, [
                m('div', { key: 'header', class: tableHeader }, [
                  m('div', { class: col(0.8) }, 'Version'),
                  m('div', { class: sortableCol(3), onclick: () => toggleSort('title') }, ['Title', sortIndicator('title')]),
                  m('div', { class: sortableCol(2), onclick: () => toggleSort('tag') }, ['Tag', sortIndicator('tag')]),
                  m('div', { class: col(0.8), style: 'text-align: right;' }, 'Criteria'),
                  m('div', { class: col(4) }, 'Description'),
                ]),
                ...sortedRubrics().map(rubric => {
                  const tag = getTag(rubric.tag_id)
                  return m('div', {
                    key: rubric.id,
                    class: tableRow,
                    onclick: () => openEdit(rubric),
                  }, [
                    m('div', { class: col(0.8) }, m('span', { class: versionBadge }, `v${rubric.current_version || 1}`)),
                    m('div', { class: col(3) }, rubric.title),
                    m('div', { class: col(2) },
                      tag
                        ? m('span', { class: tagChip, style: `background: ${tag.color || colors.border}; color: ${colors.text};` }, tag.name)
                        : '\u2014'),
                    m('div', { class: col(0.8), style: 'text-align: right;' }, rubric.criteria_count || 0),
                    m('div', { class: col(4), style: `color: ${colors.textSecondary};` }, rubric.description || ''),
                  ])
                }),
              ]),

        // --- Create/Edit form modal ---
        showForm
          ? m('div', {
              class: formOverlay,
              onclick: (e) => { if (e.target === e.currentTarget) closeForm() },
            }, [
              m('div', { class: formCard }, [
                m('div', { class: formTitle },
                  editingRubric ? `Edit rubric (creates v${(editingRubric.versions?.[0]?.version || 1) + 1})` : 'New rubric'),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Title'),
                  m('input', {
                    class: input,
                    value: formData.title,
                    oninput: (e) => { formData.title = e.target.value },
                    placeholder: 'e.g., Accuracy Assessment',
                    autofocus: true,
                  }),
                ]),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Description (optional)'),
                  m('textarea', {
                    class: input,
                    style: 'min-height: 60px; resize: vertical;',
                    value: formData.description,
                    oninput: (e) => { formData.description = e.target.value },
                    placeholder: 'What does this rubric evaluate?',
                  }),
                ]),

                tagList.length > 0
                  ? m('div', { class: formRow }, [
                      m('label', { class: label }, 'Associated tag (optional)'),
                      m('select', {
                        class: input,
                        value: formData.tagId,
                        onchange: (e) => { formData.tagId = e.target.value },
                      }, [
                        m('option', { value: '' }, 'None'),
                        ...tagList.map(t => m('option', { value: t.id }, t.name)),
                      ]),
                    ])
                  : null,

                // Criteria builder
                m('div', { class: criteriaSection }, [
                  m('label', { class: label }, 'Criteria'),
                  formData.criteria.map((criterion, idx) =>
                    m('div', {
                      key: idx,
                      'data-criterion-index': idx,
                      class: [
                        criterionRow,
                        dragIndex === idx ? criterionRowDragging : '',
                        dragOverIndex === idx && dragIndex !== idx ? criterionRowDragOver : '',
                      ].filter(Boolean).join(' '),
                      draggable: false,  // only handle via drag handle
                      ondragover: (e) => onDragOver(idx, e),
                      ondrop: (e) => onDrop(idx, e),
                    }, [
                      // Drag handle
                      m('div', {
                        class: dragHandle,
                        draggable: true,
                        ondragstart: (e) => onDragStart(idx, e),
                        ondragend: onDragEnd,
                        title: 'Drag to reorder',
                      }, '\u2261'),
                      // Number
                      m('div', { class: criterionNumber }, idx + 1),
                      // Description textarea
                      m('textarea', {
                        class: criterionDesc,
                        rows: 1,
                        value: criterion.description,
                        oninput: (e) => {
                          criterion.description = e.target.value
                          autoGrow(e)
                        },
                        onkeydown: (e) => onCriterionKeydown(e, idx),
                        oncreate: (vnode) => {
                          // Auto-size on initial render
                          const el = vnode.dom
                          el.style.height = 'auto'
                          el.style.height = el.scrollHeight + 'px'
                        },
                        onupdate: (vnode) => {
                          const el = vnode.dom
                          el.style.height = 'auto'
                          el.style.height = el.scrollHeight + 'px'
                        },
                        placeholder: `Criterion ${idx + 1}...`,
                      }),
                      // Criticality selector
                      m('select', {
                        class: criticalitySelect,
                        value: criterion.criticality,
                        onchange: (e) => { criterion.criticality = e.target.value },
                      }, [
                        m('option', { value: 'critical' }, 'Critical'),
                        m('option', { value: 'important' }, 'Important'),
                        m('option', { value: 'info' }, 'Info'),
                      ]),
                      // Remove button
                      m('button', {
                        class: removeBtn,
                        onclick: () => removeCriterion(idx),
                        disabled: formData.criteria.length <= 1,
                        title: 'Remove criterion',
                      }, '\u00d7'),
                    ])
                  ),
                  m('button', {
                    class: addCriterionBtn,
                    onclick: () => addCriterion(),
                  }, '+ Add criterion'),

                  m('div', { class: shortcutHint }, [
                    'Enter = new criterion below',
                    ' \u00b7 ',
                    'Backspace on empty = remove',
                    ' \u00b7 ',
                    'Alt+\u2191\u2193 = reorder',
                    ' \u00b7 ',
                    'Drag \u2261 handle to rearrange',
                  ]),
                ]),

                // Change summary (only for edits)
                editingRubric
                  ? m('div', { class: formRow, style: `margin-top: ${space.md};` }, [
                      m('label', { class: label }, 'Change summary (optional)'),
                      m('input', {
                        class: changeSummaryInput,
                        value: formData.changeSummary,
                        oninput: (e) => { formData.changeSummary = e.target.value },
                        placeholder: 'What changed in this version?',
                      }),
                    ])
                  : null,

                m('div', { class: formActions }, [
                  m('button', { class: btnSecondary, onclick: closeForm }, 'Cancel'),
                  m('button', {
                    class: btnPrimary,
                    disabled: saving || !formData.title.trim() ||
                      !formData.criteria.some(c => c.description.trim()),
                    onclick: submitForm,
                  }, saving
                    ? 'Saving...'
                    : editingRubric ? 'Save new version' : 'Create'),
                ]),
              ]),
            ])
          : null,
      ])
    },
  }
}
